<template>
    <div :id="id" :class="['portuna-box', {'is-open': !collapsed}]" ref="root">
        <div class="portuna-box-headline-wrapper radius" :class="titleType">
            <div class="portuna-box-headline no-select" v-if="showTitle" :class="[{pointer: isCollapsible}, titleType]" @click="toggle">
                <slot :collapsed="collapsed" :toggle="toggle" name="title">
                    <i v-if="titleIcon" class="material-icons" v-text="titleIcon" aria-hidden="true" role="img"></i>
                    <component :is="titleTag">{{title}}</component>
                </slot>
            </div>
            <div v-if="showAdditionally" class="portuna-box-additionally no-select" :class="[titleType, {'flex-vertical-middle': additionallyMiddle}]">
                <slot :collapsed="collapsed" :toggle="toggle" name="additionally"/>
                <span v-if="isCollapsible" v-interact="toggle" class="icon-interact collapse-toggle">
                    <i aria-hidden="true" class="material-icons" role="img">{{collapsed ? 'keyboard_arrow_down' : 'keyboard_arrow_up'}}</i>
                </span>
            </div>
        </div>
        <div class="portuna-box-content" v-bind="contentAttributes" v-if="renderCollapsed || !collapsed" v-show="!collapsed">
            <slot/>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {scrollToContentElement} from '@/library/dom';
    import {exportEnabled} from '@/library/export';
    import {uniqueId} from '@/library/string';
    import {computed, nextTick, reactive, ref} from 'vue';
    import {useStore} from 'vuex';
    import MediaQueryHandler from '../library/mediaQueryHandler';

    const emit  = defineEmits<{
        toggle: [uid: boolean];
    }>();
    const props = withDefaults(
        defineProps<{
            id?: Nullable<string>;
            collapsible?: boolean | MediaQueryHandlerQuery[];
            group?: string;
            defaultCollapsed?: boolean;
            renderCollapsed?: boolean;
            title?: string;
            titleIcon?: string;
            titleType?: string;
            contentAttributes?: {
                [key: string]: string;
            };
            titleTag?: string;
            scrollTo?: boolean;
            additionallyMiddle?: boolean;
        }>(),
        {
            id: null,
            collapsible: false,
            group: '',
            defaultCollapsed: false,
            renderCollapsed: true,
            title: '',
            titleIcon: '',
            titleType: 'dark',
            titleTag: exportEnabled() ? 'h3' : 'h2',
            scrollTo: false,
            additionallyMiddle: false,
        },
    );
    const slots = defineSlots<{
        default: unknown;
        title: unknown;
        additionally: unknown;
    }>();

    // use prop id if it contains a number
    // if an element gets destroyed in setup composition api, the original _uid can't be received if it gets re-rendered, thus an external identifier is required
    const uid       = props.id && String(props.id).match(/\d/) ? `_id${props.group}-${props.id}` : uniqueId();
    const root      = ref(null as Nullable<HTMLElement>);
    const store     = useStore();
    const media     = reactive<PortunaBoxMedium[]>([]);
    const groupName = computed((): string =>
    {
        return (
            props.group || 'group-' + uid
        );
    });

    const isCollapsible = computed((): boolean =>
    {
        if (props.collapsible === true)
        {
            return true;
        }
        for (let medium of media)
        {
            if (medium.active)
            {
                return true;
            }
        }
        return false;
    });

    const openedState = computed(() =>
    {
        return store.state.portunaBoxOpened[groupName.value];
    });

    const collapsed = computed((): boolean =>
    {
        return isCollapsible.value && openedState.value !== uid;
    });

    const showTitle = computed((): boolean =>
    {
        return !!(props.title.length || slots.title);
    });

    const unmounted = (): void =>
    {
        for (let medium of media)
        {
            MediaQueryHandler.remove(medium.query, medium.uuid);
        }
        if (!collapsed.value)
        {
            store.commit('removePortunaBoxOpened', {
                group: groupName.value,
            });
        }
    };

    const toggleMedia = (query: MediaQueryHandlerQuery, active: boolean): void =>
    {
        for (let medium of media)
        {
            if (medium.query === query)
            {
                medium.active = active;
                break;
            }
        }
    };

    const toggle = (): void =>
    {
        if (!isCollapsible.value || !groupName.value)
        {
            return;
        }

        let collapseUid = '';

        if (collapsed.value)
        {
            collapseUid = uid;
        }

        store.commit('updatePortunaBoxOpened', {
            group: groupName.value,
            uid: collapseUid,
        });

        emit('toggle', !!collapseUid);

        if (collapseUid && props.scrollTo)
        {
            nextTick().then(() =>
            {
                scrollToContentElement(root.value!);
            });
        }
    };

    const open = () =>
    {
        if (isCollapsible.value && collapsed.value)
        {
            toggle();
        }
    };

    // don't use as computed since $slots are not reactive - method can be used in template without brackets just like computed vars
    const showAdditionally = (): boolean =>
    {
        return isCollapsible.value || typeof slots.additionally !== 'undefined';
    };

    let collapsibleQueries = Array.isArray(props.collapsible) && props.collapsible.length;

    if (collapsibleQueries)
    {
        for (let query of <MediaQueryHandlerQuery[]>props.collapsible)
        {
            let medium: PortunaBoxMedium = {
                query,
                uuid: '',
                active: false,
            };

            media.push(medium);

            medium.uuid = MediaQueryHandler.on(query, {
                match: () => toggleMedia(query, true),
                unmatch: () => toggleMedia(query, false),
            });
        }
    }

    if (
        (collapsibleQueries || props.collapsible === true) &&
        !props.defaultCollapsed &&
        !openedState.value
    )
    {
        store.commit('updatePortunaBoxOpened', {
            group: groupName.value,
            uid: uid,
        });
    }

    const currentHash = window.location.hash;

    if ('#' + props.id === currentHash)
    {
        // reset hash
        window.location.hash = '';
        // re-set hash after short while to re-trigger scroll
        window.setTimeout(() =>
        {
            window.location.hash = currentHash;
        }, 100);
    }

    defineExpose({open});
</script>